import { useState } from "react";
import { ROLE_LABELS, roleClass } from "@/lib/api";
import { BookOpen, ChevronDown, Printer, Newspaper, Vote, IdCard, CalendarDays, MessageCircle, Wallet, HandCoins, Receipt, Users, Settings, Bell, FileSignature, Smartphone, Radio } from "lucide-react";

// Ogni capitolo dichiara chi lo vede: "all" oppure una lista di ruoli. Gli admin vedono tutto.
const CHAPTERS = [
  {
    id: "start", icon: Smartphone, title: "Primi passi", who: "all",
    steps: [
      "Accedi con il tuo account Google: è lo stesso indirizzo email usato per l'iscrizione.",
      "Sul telefono installa l'app: Android → banner \"Installa\"; iPhone → Safari, tocca Condividi → Aggiungi a Home.",
      "Attiva le notifiche col tasto \"Attiva notifiche\" (menu a sinistra o Community): riceverai avvisi su riunioni, votazioni e news.",
      "Il menu in alto a destra (☰) sul telefono apre tutte le sezioni.",
    ],
  },
  {
    id: "news", icon: Newspaper, title: "News", who: "all",
    steps: [
      "Qui trovi comunicati, eventi e annunci dell'Associazione, con le prossime riunioni in evidenza.",
      "Usa la barra di ricerca per filtrare per titolo o tag.",
    ],
    admin: ["Consiglio: \"Nuova News\" pubblica su app e sito WordPress e invia una notifica push a tutti i soci."],
  },
  {
    id: "voting", icon: Vote, title: "Votazioni", who: "all",
    steps: [
      "Le votazioni aperte compaiono in alto: scegli un'opzione e conferma. Il voto è unico e non modificabile.",
      "Vedi solo le votazioni destinate al tuo ruolo (o a tutti). I risultati appaiono a chiusura.",
      "Riceverai una notifica quando si apre una votazione e un promemoria nelle ultime 24 ore se non hai ancora votato.",
    ],
    admin: ["Consiglio: crea la votazione scegliendo destinatari (tutti / una sezione) e data di chiusura; puoi chiuderla manualmente e generare il verbale con sigillo digitale."],
  },
  {
    id: "membership", icon: IdCard, title: "La mia tessera", who: "all",
    steps: [
      "Nella sezione Tesseramenti trovi la tua tessera digitale con numero, scadenza e PDF scaricabile (con QR di verifica).",
      "Inserisci il numero WhatsApp e i dati fiscali (Codice Fiscale, indirizzo): servono per la ricevuta della quota.",
      "Riceverai email e notifica 30/15/7/1 giorni prima della scadenza della tessera.",
    ],
  },
  {
    id: "calendar", icon: CalendarDays, title: "Calendario", who: "all",
    steps: [
      "Le riunioni sono divise per sezione (Tutti, Consiglio, Fondatori, Soci, Collaboratori).",
      "\"Iscriviti al Calendario Amunì\" aggiunge il calendario dell'Associazione al tuo Google Calendar. Su iPhone usa il tasto \"iPhone / Apple / Outlook\".",
      "Su ogni riunione: \"Aggiungi a Google Calendar\" salva solo quell'evento; \"Parteciperai?\" conferma la presenza (Sì / Forse / No).",
      "Promemoria automatici 24 ore e 1 ora prima via email e notifica.",
    ],
    admin: ["Consiglio: \"Nuova Riunione\" crea l'evento anche su Google Calendar e notifica la sezione; il tasto WhatsApp prepara il messaggio per il gruppo."],
  },
  {
    id: "community", icon: MessageCircle, title: "Community", who: "all",
    steps: [
      "\"Vai al Gruppo\" apre il gruppo WhatsApp della Community.",
      "Qui trovi anche il tasto per attivare/disattivare le notifiche.",
    ],
    admin: ["Consiglio: componi un messaggio (o usa un template) e \"Apri WhatsApp\": scegli il gruppo e invia. La card \"Notifica push\" invia un avviso diretto sui telefoni dei soci, filtrando per sezione."],
  },
  {
    id: "transparency", icon: Wallet, title: "Trasparenza", who: "all",
    steps: [
      "Bilancio & Spese: entrate, uscite e saldo dell'anno, separati tra Cassa (contanti) e Banca (POS/bonifico).",
      "Documenti Ufficiali: statuto, bilanci e altri atti scaricabili.",
      "Verbali Riunioni: verbali firmati con sigillo digitale (SHA-256 + QR) verificabile.",
    ],
  },
  {
    id: "donations", icon: HandCoins, title: "Donazioni", who: "all",
    steps: [
      "Puoi sostenere l'Associazione col link PayPal. Dopo la conferma ricevi via email la ricevuta con link al PDF.",
    ],
    admin: ["Consiglio/Tesoriere: le donazioni arrivate vanno confermate qui (\"Conferma\"): questo genera numero ricevuta, registra l'entrata in Trasparenza e invia l'email al donatore."],
  },
  {
    id: "receipts", icon: Receipt, title: "Ricevute di pagamento", who: ["tesoriere"],
    steps: [
      "\"Ricevute da trasmettere\": elenco dei soci attivi senza ricevuta quota per l'anno. Verifica la prova di pagamento (\"Vedi pagamento\") e premi \"Emetti ricevuta\": il modulo si precompila con i dati del socio.",
      "\"Nuova Ricevuta\": compila destinatario, importo, causale (quota / contributo / corso) e modalità. L'invio email è automatico se c'è l'indirizzo.",
      "La numerazione è progressiva per anno (001/2026…). L'entrata viene registrata in Trasparenza nel portafoglio corretto (Cassa o Banca).",
      "\"Annulla\" invalida una ricevuta con motivazione mantenendo il numero; compare nel Report Fiscale.",
      "\"Reinvia tutte via email\" rimanda ai soci tutte le ricevute valide dell'anno selezionato.",
      "\"Report Fiscale\" genera il PDF annuale per il commercialista con registro Cassa/Banca e ricevute annullate.",
    ],
  },
  {
    id: "ledger", icon: Wallet, title: "Gestione bilancio", who: ["tesoriere"],
    steps: [
      "In Trasparenza → \"Aggiungi\" registra entrate/uscite manuali indicando categoria, portafoglio e data.",
      "Le voci generate da ricevute e donazioni sono automatiche: non vanno duplicate.",
      "Usa il filtro portafoglio per la riconciliazione con l'estratto conto bancario.",
    ],
  },
  {
    id: "members", icon: Users, title: "Soci & Ruoli", who: ["tesseramento", "segretario", "tesoriere"],
    steps: [
      "Sezione Soci: le richieste \"In attesa\" arrivano dal modulo del sito. Controlla il documento d'identità (fronte e retro) e l'eventuale prova di pagamento caricata.",
      "\"Approva\" attiva il socio, invia email di benvenuto + notifica. La ricevuta della quota NON è automatica: la emette il Tesoriere da Ricevute → \"Ricevute da trasmettere\".",
      "Assegna il ruolo dal menu a tendina: determina cosa vede e per cosa vota.",
      "\"Registro Soci PDF\" produce il libro soci annuale con sigillo digitale. \"Richiedi CF ai soci\" manda un'email a chi non ha il Codice Fiscale.",
    ],
  },
  {
    id: "verbali", icon: FileSignature, title: "Verbali", who: ["segretario"],
    steps: [
      "Da una votazione chiusa o da Trasparenza → Verbali, genera il verbale: ordine del giorno, presenti, esito.",
      "Il PDF include firma del Presidente (se caricata in Impostazioni), hash SHA-256 e QR di verifica pubblica.",
    ],
  },
  {
    id: "live", icon: Radio, title: "Riunione Live (Assemblea in diretta)", who: "all",
    steps: [
      "Quando il Segretario avvia una riunione, sulla card del Calendario compare il tasto rosso lampeggiante \"Riunione in corso — Entra\": toccalo per partecipare dal telefono o dal PC.",
      "Votazioni: se sei tra i presenti segnati dal Segretario, quando si apre una votazione scegli la tua opzione (es. Sì / No / Astenuto). Il voto è nominale e i risultati si aggiornano in diretta.",
      "Chiedi la parola: nel riquadro \"Richieste di intervento\" scrivi l'Argomento e (se vuoi) cosa vuoi dire, poi premi \"Chiedi la parola\". La tua richiesta entra in coda e il Segretario la approva in ordine.",
      "Se non sei ancora tra i presenti, vedrai l'avviso di attendere che il Segretario ti segni presente.",
    ],
    admin: [
      "Segretario/Consiglio: sulla riunione del giorno premi \"Avvia Live\" (la sezione della riunione decide chi può partecipare — es. una riunione Fondatori mostra i fondatori in su).",
      "Presenti: nel riquadro \"Presenti alla riunione\" spunta chi è in sala, scegliendo tra i soli soci abilitati per quel target (usa la ricerca). Solo i presenti possono votare e intervenire.",
      "Votazione: \"Apri votazione\" → scrivi la domanda (opzioni di default Sì/No/Astenuto, modificabili una per riga) → i presenti votano → \"Chiudi votazione\" quando basta.",
      "Interventi: per ogni richiesta usa \"Dai la parola\" (In parola), \"Rifiuta\" o \"Concluso\".",
      "Termina: \"Termina\" chiude la riunione e apre il VERBALE DEL GIORNO già precompilato (presenti, esiti votazioni con nomi, interventi). Rivedi, correggi e \"Salva verbale\": finisce in Trasparenza → Verbali con sigillo digitale.",
    ],
  },
  {
    id: "settings", icon: Settings, title: "Impostazioni", who: [],
    steps: [
      "Dati Associazione (nome, indirizzo, C.F., email) usati su tessere, ricevute e PDF.",
      "Presidente e firma digitale (immagine) per i verbali.",
      "Google Calendar ID condiviso, link PayPal, link gruppo WhatsApp Community.",
      "Snippet WordPress: iscrizione, news, eventi, trasparenza, statistiche da incollare nel sito.",
    ],
  },
  {
    id: "notifications", icon: Bell, title: "Notifiche: cosa arriva e quando", who: "all",
    steps: [
      "Email + notifica push: nuova news, nuova votazione, nuova riunione, promemoria riunione (24h e 1h), votazione in chiusura, approvazione iscrizione, scadenza tessera, ricevute.",
      "Le notifiche push funzionano solo se attivate dal tasto \"Attiva notifiche\" sul dispositivo (su iPhone con app aggiunta alla Home).",
      "Se non arrivano: controlla i permessi notifiche del browser/telefono per app.amunicassibile.it.",
    ],
  },
];

const ADMIN_ROLES = ["consiglio"];

function visible(ch, user) {
  const isAdmin = user.is_admin || ADMIN_ROLES.includes(user.role);
  if (isAdmin) return true;
  if (ch.who === "all") return true;
  return Array.isArray(ch.who) && ch.who.includes(user.role);
}

function Chapter({ ch, isAdmin, open, toggle }) {
  const Icon = ch.icon;
  return (
    <div className="paper rounded-2xl overflow-hidden print:break-inside-avoid" data-testid={`guide-chapter-${ch.id}`}>
      <button onClick={toggle} className="w-full flex items-center gap-3 p-4 sm:p-5 text-left hover:bg-[#FAF7F2] transition" data-testid={`guide-toggle-${ch.id}`}>
        <div className="w-9 h-9 rounded-xl bg-[#FDF2F2] text-[#EF3E36] flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></div>
        <div className="font-display text-base sm:text-lg font-bold text-[#2B3A33] flex-1 min-w-0">{ch.title}</div>
        <ChevronDown className={`w-4 h-4 text-[#8A8272] transition-transform print:hidden ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-5 -mt-1">
          <ol className="list-decimal list-outside pl-5 space-y-2 text-sm text-[#3C4A44] leading-relaxed">
            {ch.steps.map((s, i) => <li key={`${s.slice(0,24)}-${i}`}>{s}</li>)}
          </ol>
          {isAdmin && ch.admin && (
            <div className="mt-3 text-xs bg-[#FEF3C7] border-l-4 border-[#D97706] text-[#78350F] rounded-lg p-3 space-y-1">
              {ch.admin.map((a, i) => <p key={`${a.slice(0,24)}-${i}`}>{a}</p>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Guide({ user }) {
  const isAdmin = user.is_admin || ADMIN_ROLES.includes(user.role);
  const chapters = CHAPTERS.filter(ch => visible(ch, user));
  const [openIds, setOpenIds] = useState(() => new Set([chapters[0]?.id]));
  const [allOpen, setAllOpen] = useState(false);
  const toggle = (id) => setOpenIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const expandAll = () => { setAllOpen(true); setOpenIds(new Set(chapters.map(c => c.id))); };
  const print = () => { expandAll(); setTimeout(() => window.print(), 300); };

  return (
    <div data-testid="section-guide">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#EF3E36] font-bold flex items-center gap-2"><BookOpen className="w-3 h-3" /> Manuale</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2B3A33] mt-1">Come funziona Amunì</h1>
          <p className="text-[#6B6558] mt-2">
            Guida personalizzata per il tuo ruolo:&nbsp;
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${roleClass(user.role)}`} data-testid="guide-role-badge">{ROLE_LABELS[user.role] || user.role}</span>
            {isAdmin && <span className="text-xs text-[#8A8272]"> · vedi anche i riquadri gialli riservati al Consiglio</span>}
          </p>
        </div>
        <div className="flex gap-2 print:hidden">
          <button onClick={allOpen ? () => { setAllOpen(false); setOpenIds(new Set()); } : expandAll} className="px-4 py-2.5 rounded-xl border border-[#E8E2D8] bg-white text-sm font-semibold hover:bg-[#F3EEE4]" data-testid="guide-expand-all">
            {allOpen ? "Chiudi tutto" : "Apri tutto"}
          </button>
          <button onClick={print} className="px-4 py-2.5 rounded-xl bg-[#2B3A33] text-white text-sm font-semibold inline-flex items-center gap-2 hover:opacity-90" data-testid="guide-print">
            <Printer className="w-4 h-4" /> Stampa / PDF
          </button>
        </div>
      </div>
      <div className="grid gap-3">
        {chapters.map(ch => <Chapter key={ch.id} ch={ch} isAdmin={isAdmin} open={openIds.has(ch.id)} toggle={() => toggle(ch.id)} />)}
      </div>
    </div>
  );
}
